import { BenefitTypeOptions, BenefitTypes } from '@strata/constants';
import { FormContainer } from '@strata/react-form-container';
import { Spinner } from '@strata/react-loading-animation';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';

import { postWorklistTask } from 'api/oopsAPI';
import { handleError } from 'services/Error';
import { useGetValueCaseListQuery } from 'store/api/oopsApi';
import { useGetPayerSegmentListQuery, useGetSupplierListQuery } from 'store/api/referenceApi';

const ValidationSchema = Yup.object({
    payer_id: Yup.string().required(validationStrings.requiredField.errorMessage),
    segment_id: Yup.string().required(validationStrings.requiredField.errorMessage),
    benefit: Yup.string()
        .oneOf(Object.values(BenefitTypes))
        .required(validationStrings.requiredField.errorMessage),
    value_case: Yup.object({
        value_case_id: Yup.string().required(validationStrings.requiredField.errorMessage),
        segment_id: Yup.string().required(),
        product_service_incentives: Yup.array(
            Yup.object({ product_service_oid: Yup.string().required() }),
        ).required(),
    }).required(),
    product_service_oid: Yup.string().required(validationStrings.requiredField.errorMessage),
    supplier_npi: Yup.string().required(validationStrings.requiredField.errorMessage),
});

export const WorklistTask = () => {
    const [generatedReferral, setGeneratedReferral] = useState<object>();

    const { data: payerSegmentList, isFetching: payerSegmentListFetching } =
        useGetPayerSegmentListQuery();

    const { data: valueCaseList, isFetching: valueCaseListFetching } = useGetValueCaseListQuery();
    const { data: supplierList, isFetching: supplierListFetching } = useGetSupplierListQuery();

    return (
        <Formik
            initialValues={
                { benefit: BenefitTypes.PHARMACY } as Yup.InferType<typeof ValidationSchema>
            }
            validationSchema={ValidationSchema}
            onSubmit={async (values) => {
                try {
                    const response = await postWorklistTask(values);
                    SuccessToast({ message: 'Worklist task created.' });
                    setGeneratedReferral(response);
                } catch (err) {
                    handleError('Error creating worklist task.', err as Error);
                }
            }}
        >
            {(formik) => (
                <FormContainer
                    formTitle="Create Worklist Task"
                    primaryButtonLabel="Create Worklist Task"
                    onFormSubmit={formik.handleSubmit}
                    isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                    onCancelClick={formik.resetForm}
                    formLeftContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="segment_id"
                                label="Payer Segment"
                                required
                                isLoading={payerSegmentListFetching}
                                values={payerSegmentList}
                                selectedItem={payerSegmentList?.find(
                                    ({ segmentId }) => segmentId === formik.values.segment_id,
                                )}
                                onChange={(newValue) => {
                                    void (async () => {
                                        await formik.setFieldValue('payer_id', newValue?.payerId);
                                        await formik.setFieldValue(
                                            'segment_id',
                                            newValue?.segmentId,
                                        );
                                        await formik.setFieldValue('value_case', undefined);
                                    })();
                                }}
                                helperText="Payer segment to manage routes for"
                            />
                            <RadioButtonGroupFormik
                                name="benefit"
                                label="Benefit"
                                options={BenefitTypeOptions.map(({ value, name }) => ({
                                    value,
                                    text: name,
                                }))}
                                required
                            />
                            <SuggestSingleSelectFormik
                                id="value_case.value_case_id"
                                label="Value Case"
                                required
                                isLoading={valueCaseListFetching}
                                values={valueCaseList?.filter(
                                    ({ segment_id }) => segment_id === formik.values.segment_id,
                                )}
                                selectedItem={valueCaseList?.find(
                                    ({ value_case_id }) =>
                                        value_case_id === formik.values.value_case?.value_case_id,
                                )}
                                onChange={(newValue) => {
                                    void (async () => {
                                        await formik.setFieldValue('value_case', newValue);
                                        await formik.setFieldValue(
                                            'product_service_oid',
                                            undefined,
                                        );
                                    })();
                                }}
                            />
                            <SuggestSingleSelectFormik
                                id="product_service_oid"
                                label="Product Oid"
                                required
                                values={formik.values.value_case?.product_service_incentives?.map(
                                    ({ product_service_oid }) => product_service_oid,
                                )}
                            />
                            <SuggestSingleSelectFormik
                                id="supplier_npi"
                                label="Supplier NPI"
                                required
                                isLoading={supplierListFetching}
                                values={supplierList}
                                selectedItem={supplierList?.find(
                                    ({ npi }) => npi === formik.values.supplier_npi,
                                )}
                                onChange={(newValue) => {
                                    void formik.setFieldValue('supplier_npi', newValue?.npi);
                                }}
                            />
                        </>
                    }
                    formRightContent={
                        <>
                            <div>Generated Referral:</div>
                            {formik.isSubmitting ? (
                                <Spinner intent={Spinner.Intent.NONE} />
                            ) : (
                                <pre>
                                    {generatedReferral &&
                                        JSON.stringify(generatedReferral, null, 2)}
                                </pre>
                            )}
                        </>
                    }
                />
            )}
        </Formik>
    );
};
